import React, { useRef, useEffect, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getBounds, getCenter } from "geolib";
import { useMapAuth, MAP_STYLE_URL } from "./useMapAuth";

const ZOOM_FACTOR_PADDING = 60;

export const MultiMapView = ({ markers, reloadSignal }) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const authOptions = useMapAuth();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    setIsVisible(false);
    const timeout = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timeout);
  }, [reloadSignal]);

  useEffect(() => {
    if (!authOptions || !mapContainer.current || !isVisible) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    markersRef.current = [];

    if (!markers?.length) return;

    const points = markers.map(({ coordinates: { lat, lon } }) => ({
      latitude: lat,
      longitude: lon,
    }));

    const center = getCenter(points);
    const centerLng = center ? center.longitude : 0;
    const centerLat = center ? center.latitude : 0;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: MAP_STYLE_URL,
      center: [centerLng, centerLat],
      zoom: 10,
      ...authOptions,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    map.on("load", () => {
      const bounds = new maplibregl.LngLatBounds();
      markers.forEach(({ id, placeLabel, coordinates: { lat, lon } }) => {
        bounds.extend([lon, lat]);

        const popup = new maplibregl.Popup({ offset: 25 }).setText(
          placeLabel || ""
        );

        const marker = new maplibregl.Marker()
          .setLngLat([lon, lat])
          .setPopup(popup)
          .addTo(map);

        markersRef.current.push(marker);
      });

      if (markers.length > 1) {
        map.fitBounds(bounds, { padding: ZOOM_FACTOR_PADDING });
      }
    });

    mapRef.current = map;

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [authOptions, markers, isVisible]);

  if (!isVisible || !markers?.length) return null;

  return (
    <div
      ref={mapContainer}
      style={{ width: "100%", height: "100%", minHeight: 300 }}
    />
  );
};
